import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box } from '@mui/system';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { Document, pdfjs, Page as Pages } from 'react-pdf';
import { useDispatch } from 'react-redux';
import { BsTelephone } from 'react-icons/bs';
import { GiModernCity } from 'react-icons/gi';
import { FiCheck, FiX, FiCheckCircle } from 'react-icons/fi';
// material icons
import DeckIcon from '@mui/icons-material/Deck';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmergencyShareIcon from '@mui/icons-material/EmergencyShare';
import VillaIcon from '@mui/icons-material/Villa';
import { BiCloudDownload } from 'react-icons/bi';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import ViewCardComponent from '../../components/ViewCards';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getPartnerStatusType,
  PreviewImageApi,
  saveApiData
} from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useOnboardingSelector from '../../store/Slice/Onboarding/OnboardingSelector';
import { getOnboardingView } from '../../store/Slice/Onboarding/OnboardingSlice';
import { OnboardingApi } from '../../utils/Apis';
import { normalizeValue } from '../../utils/CommonFunction';
import { NOT_AVAILABLE } from '../Global/Constants';
import cancelChequeDummy from './dummy/cancelChequeDummy.png';
import photoDummy from './dummy/photoDummy.jpg';
import scan from './dummy/scan.png';
import aadharFrontDummy from './dummy/aadharFrontDummy.jpg';
import panCardDummy from './dummy/panCardDummy.jpg';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function ReviewOnBoarding() {
  const [pageNumber, setPageNumber] = useState(1);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [previewFileType, setPreviewFileType] = useState('image');
  const [qrAppBlobUrl, setQrAppBlobUrl] = useState(null);
  const [qrOnboardBlobUrl, setQrOnboardBlobUrl] = useState(null);
  const [docBlobUrls, setDocBlobUrls] = useState({});

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { rowId } = location?.state || {};

  const { LoginData } = useLoginSelector();
  const { PartnerStatusTypes } = useGlobalSelector();
  const { OnboardingViewData: reviewData } = useOnboardingSelector();

  useEffect(() => {
    dispatch(getPartnerStatusType());
  }, [dispatch]);

  const onDocumentLoadSuccess = useCallback(() => {
    setPageNumber(1);
  }, []);

  const fireUpdateStateApi = useCallback(
    async (typeid) => {
      if (!typeid) return;
      try {
        const statusCredential = {
          companyId: LoginData?.companyId,
          partnerId: rowId,
          partnerStatusTypeId: typeid
        };
        const result = await dispatch(saveApiData(OnboardingApi?.onboardUpdate, statusCredential));
        if (result?.status === 'success') navigate('/onboarding');
      } catch (error) {
        console.error('Failed to update partner status', error);
      }
    },
    [LoginData, rowId, dispatch, navigate]
  );

  useEffect(() => {
    const credential = {
      companyId: LoginData?.companyId || '',
      partnerId: rowId
    };
    dispatch(getOnboardingView(credential));
  }, [LoginData, rowId, dispatch]);

  useEffect(() => {
    if (reviewData?.qrCodeApplicationUrl) {
      const fetchAppQr = async () => {
        const endpoint = reviewData.qrCodeApplicationUrl.startsWith('/')
          ? reviewData.qrCodeApplicationUrl.substring(1)
          : reviewData.qrCodeApplicationUrl;
        const blob = await dispatch(PreviewImageApi(endpoint, null, true));
        if (blob) {
          setQrAppBlobUrl(URL.createObjectURL(blob));
        }
      };
      fetchAppQr();
    }
    if (reviewData?.qrCodeOnboardUrl) {
      const fetchOnboardQr = async () => {
        const endpoint = reviewData.qrCodeOnboardUrl.startsWith('/')
          ? reviewData.qrCodeOnboardUrl.substring(1)
          : reviewData.qrCodeOnboardUrl;
        const blob = await dispatch(PreviewImageApi(endpoint, null, true));
        if (blob) {
          setQrOnboardBlobUrl(URL.createObjectURL(blob));
        }
      };
      fetchOnboardQr();
    }
    if (reviewData?.partnerDocuments?.length > 0) {
      reviewData.partnerDocuments.forEach(async (doc) => {
        if (doc?.documentUrl) {
          const endpoint = doc.documentUrl.startsWith('/')
            ? doc.documentUrl.substring(1)
            : doc.documentUrl;
          const blob = await dispatch(PreviewImageApi(endpoint, null, true));
          if (blob) {
            setDocBlobUrls((prev) => ({
              ...prev,
              [doc.partnerDocumentId]: {
                url: URL.createObjectURL(blob),
                type: blob.type
              }
            }));
          }
        }
      });
    }
  }, [reviewData, dispatch]);

  const isIndividual = reviewData?.typeId === 66;

  const infoFields = useMemo(
    () => [
      {
        icon: <DeckIcon className="viewLabelIcon" size={24} />,
        label: 'Partner Type',
        value: normalizeValue(reviewData?.partnerType?.typeCode, NOT_AVAILABLE)
      },
      {
        icon: <GroupIcon className="viewLabelIcon" size={24} />,
        label: isIndividual ? 'Name' : 'Company Name',
        value: normalizeValue(reviewData?.partnerName, NOT_AVAILABLE)
      },
      {
        icon: <PersonIcon className="viewLabelIcon" size={24} />,
        label: isIndividual ? "Father's Name" : 'Authorised Person Name',
        value: normalizeValue(
          isIndividual ? reviewData?.fathersName : reviewData?.authorisedPersonName,
          NOT_AVAILABLE
        )
      },
      {
        icon: <CalendarMonthIcon className="viewLabelIcon" size={24} />,
        label: isIndividual ? 'DOB' : 'Date of Incorporation',
        value: normalizeValue(
          reviewData?.dob ? reviewData.dob : reviewData?.dateOfIncorporation,
          NOT_AVAILABLE
        )
      },
      {
        icon: <BsTelephone className="viewLabelIcon" size={24} />,
        label: 'Phone',
        value: normalizeValue(reviewData?.mobile, NOT_AVAILABLE)
      },
      {
        icon: <EmergencyShareIcon className="viewLabelIcon" size={24} />,
        label: 'Address',
        value: normalizeValue(reviewData?.address, NOT_AVAILABLE)
      },
      {
        icon: <GiModernCity className="viewLabelIcon" size={24} />,
        label: 'City',
        value: normalizeValue(reviewData?.cityEntity?.cityName, NOT_AVAILABLE)
      },
      {
        icon: <VillaIcon className="viewLabelIcon" size={24} />,
        label: 'State',
        value: normalizeValue(reviewData?.stateEntity?.stateName, NOT_AVAILABLE)
      },
      {
        icon: <VillaIcon className="viewLabelIcon" size={24} />,
        label: 'Email Id',
        value: normalizeValue(reviewData?.emailAddress, NOT_AVAILABLE)
      }
    ],
    [reviewData, isIndividual]
  );

  async function handlePreviewDoc(partnerDocumentId) {
    const { partnerId, companyId } = reviewData;

    const blob = await dispatch(
      PreviewImageApi(`pdoc/${companyId}/${partnerId}/${partnerDocumentId}`, null, true)
    );

    if (!blob) return;

    setPreviewFileType(blob.type === 'application/pdf' ? 'pdf' : 'image');
    const fileURL = URL.createObjectURL(blob);
    setPreviewUrl(fileURL);
    setOpenPreview(true);
  }

  async function handleDownloadDoc(partnerDocumentId, fileName = 'document') {
    const { partnerId, companyId } = reviewData;
    const blob = await dispatch(
      PreviewImageApi(`pdoc/${companyId}/${partnerId}/${partnerDocumentId}`, null, true)
    );

    if (!blob) return;

    const extension = blob.type === 'application/pdf' ? 'pdf' : 'png';
    const fileURL = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileURL;
    link.setAttribute('download', `${fileName}.${extension}`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(fileURL);
  }

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const handleDoubleClick = () => {
    setZoomLevel((prev) => (prev === 1 ? 2 : 1));
  };

  return (
    <Page title="Partner Management System || Review Partner">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {reviewData && (
            <Box pt={3} pb={3} mb={4}>
              <Box sx={{ mb: 3 }}>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/onboarding')}
                  sx={{
                    color: '#64748b',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    mb: 1.5,
                    p: 0,
                    '&:hover': { color: '#0f172a', bgcolor: 'transparent' }
                  }}
                >
                  Back to Onboarding
                </Button>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{
                        color: '#0f172a',
                        fontWeight: 800,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        letterSpacing: '-0.02em',
                        mb: 0.5
                      }}
                    >
                      Review Partner
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Review partner application and verify documents
                    </Typography>
                  </Box>
                  <Box>
                    <BrudCrumbs />
                  </Box>
                </Stack>
              </Box>

              {/* Action Buttons Toolbar */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                {PartnerStatusTypes?.length > 0 &&
                  PartnerStatusTypes.map((status, index) => {
                    if (status?.typeCode === 'APPROVED') {
                      return (
                        <Tooltip key={index} title="Approve Partner" placement="top" arrow>
                          <Button
                            variant="outlined"
                            startIcon={<FiCheck />}
                            sx={{
                              color: '#16a34a',
                              borderColor: '#86efac',
                              bgcolor: 'rgba(22, 163, 74, 0.05)',
                              fontWeight: 600,
                              borderRadius: '8px',
                              textTransform: 'none',
                              '&:hover': {
                                bgcolor: 'rgba(22, 163, 74, 0.12)',
                                borderColor: '#16a34a'
                              }
                            }}
                            onClick={() => fireUpdateStateApi(status?.typeId)}
                          >
                            Approve
                          </Button>
                        </Tooltip>
                      );
                    }
                    if (status?.typeCode === 'REJECTED') {
                      return (
                        <Tooltip key={index} title="Reject Partner" placement="top" arrow>
                          <Button
                            variant="outlined"
                            startIcon={<FiX />}
                            sx={{
                              color: '#ef4444',
                              borderColor: '#fca5a5',
                              bgcolor: 'rgba(239, 68, 68, 0.05)',
                              fontWeight: 600,
                              borderRadius: '8px',
                              textTransform: 'none',
                              '&:hover': {
                                bgcolor: 'rgba(239, 68, 68, 0.12)',
                                borderColor: '#ef4444'
                              }
                            }}
                            onClick={() => fireUpdateStateApi(status?.typeId)}
                          >
                            Reject
                          </Button>
                        </Tooltip>
                      );
                    }
                    if (status?.typeCode === 'ACTIVATED') {
                      const isAllowed =
                        reviewData?.partnerStatus?.typeCode === 'ACTIVATED' ||
                        reviewData?.partnerStatus?.typeCode === 'APPROVED';
                      return (
                        <Tooltip key={index} title="Activate Partner" placement="top" arrow>
                          <span>
                            <Button
                              variant="contained"
                              disabled={!isAllowed}
                              startIcon={<FiCheckCircle />}
                              sx={{
                                bgcolor: '#13519d',
                                fontWeight: 600,
                                borderRadius: '8px',
                                textTransform: 'none',
                                '&:hover': { bgcolor: '#0d3d75' }
                              }}
                              onClick={() => fireUpdateStateApi(status?.typeId)}
                            >
                              Activate
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    }
                    return null;
                  })}
              </Box>

              {/* Partner Details Card */}
              <Grid container spacing={3} mb={3}>
                <ViewCardComponent title="Partner Details" fields={infoFields || []} />
              </Grid>

              {/* Document Verification Card */}
              <Grid item xs={12} sx={{ mt: 1, mb: 3 }}>
                <Card
                  sx={{
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    p: { xs: 2.5, md: 3 },
                    bgcolor: '#ffffff',
                    position: 'relative'
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                  >
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        bgcolor: '#0284c7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#ffffff'
                      }}
                    >
                      <FolderOpenIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                        Document Verification
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        Uploaded documents and compliance verification
                      </Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={3}>
                    {reviewData?.partnerDocuments?.map((documentElements, index) => {
                      const docType = documentElements?.documentType?.typeCode;
                      const mapping = {
                        'USER IMAGE': { label: 'User Image', dummy: photoDummy },
                        CHEQUE: { label: 'Cancelled Cheque Image', dummy: cancelChequeDummy },
                        'AADHAR CARD': { label: 'Aadhar Card Image', dummy: aadharFrontDummy },
                        'PAN CARD': { label: 'PAN Card Image', dummy: panCardDummy }
                      };

                      const config = mapping[docType];
                      if (!config) return null;

                      const fetchedDoc = docBlobUrls[documentElements.partnerDocumentId];
                      const isPdf = fetchedDoc?.type === 'application/pdf';
                      const imgSrc = fetchedDoc && !isPdf ? fetchedDoc.url : config.dummy;

                      return (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0',
                              bgcolor: '#f8fafc',
                              textAlign: 'center'
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}
                            >
                              {config.label}
                            </Typography>

                            <Box
                              className="uploadDocBoxLabel uploadDocBoxLabelFull docPreviewBox"
                              sx={{
                                height: 160,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #cbd5e1',
                                bgcolor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}
                              onClick={() => {
                                if (fetchedDoc) {
                                  setPreviewFileType(isPdf ? 'pdf' : 'image');
                                  setPreviewUrl(fetchedDoc.url);
                                  setOpenPreview(true);
                                } else {
                                  handlePreviewDoc(documentElements?.partnerDocumentId);
                                }
                              }}
                            >
                              <Box className="previewIconsBox">
                                <Tooltip title="Preview" arrow>
                                  <Box
                                    className="previewEyeIcon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (fetchedDoc) {
                                        setPreviewFileType(isPdf ? 'pdf' : 'image');
                                        setPreviewUrl(fetchedDoc.url);
                                        setOpenPreview(true);
                                      } else {
                                        handlePreviewDoc(documentElements?.partnerDocumentId);
                                      }
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </Box>
                                </Tooltip>
                                <Tooltip title="Download" arrow>
                                  <Box
                                    className="previewEyeIcon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (fetchedDoc) {
                                        const extension = isPdf ? 'pdf' : 'png';
                                        const link = document.createElement('a');
                                        link.href = fetchedDoc.url;
                                        link.setAttribute(
                                          'download',
                                          `${config.label}.${extension}`
                                        );
                                        document.body.appendChild(link);
                                        link.click();
                                        link.parentNode.removeChild(link);
                                      } else {
                                        handleDownloadDoc(
                                          documentElements?.partnerDocumentId,
                                          config.label
                                        );
                                      }
                                    }}
                                  >
                                    <BiCloudDownload size={16} />
                                  </Box>
                                </Tooltip>
                              </Box>
                              <img
                                src={imgSrc}
                                alt={config.label}
                                className="previewImage"
                                style={{ maxHeight: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Card>
              </Grid>

              {/* QR Code Documents */}
              {(reviewData?.qrCodeApplicationUrl || reviewData?.qrCodeOnboardUrl) && (
                <Grid item xs={12}>
                  <Card
                    sx={{
                      borderRadius: '16px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                      p: { xs: 2.5, md: 3 },
                      bgcolor: '#ffffff',
                      position: 'relative'
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: '#0284c7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff'
                        }}
                      >
                        <QrCodeScannerIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                          QR Code Documents
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                          Application and Onboarding generated QR Codes
                        </Typography>
                      </Box>
                    </Stack>

                    <Grid container spacing={3}>
                      {reviewData?.qrCodeApplicationUrl && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0',
                              bgcolor: '#f8fafc',
                              textAlign: 'center'
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}
                            >
                              QR Code Application
                            </Typography>
                            <Box
                              className="uploadDocBoxLabel uploadDocBoxLabelFull docPreviewBox"
                              sx={{
                                height: 160,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #cbd5e1',
                                bgcolor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}
                              onClick={() => {
                                if (qrAppBlobUrl) {
                                  setPreviewFileType('image');
                                  setPreviewUrl(qrAppBlobUrl);
                                  setOpenPreview(true);
                                }
                              }}
                            >
                              <Box className="previewIconsBox">
                                <Tooltip title="Preview" arrow>
                                  <Box
                                    className="previewEyeIcon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (qrAppBlobUrl) {
                                        setPreviewFileType('image');
                                        setPreviewUrl(qrAppBlobUrl);
                                        setOpenPreview(true);
                                      }
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </Box>
                                </Tooltip>
                                <Tooltip title="Download" arrow>
                                  <Box
                                    className="previewEyeIcon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (qrAppBlobUrl) {
                                        const link = document.createElement('a');
                                        link.href = qrAppBlobUrl;
                                        link.setAttribute('download', 'QRCodeApplication.png');
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }
                                    }}
                                  >
                                    <BiCloudDownload size={16} />
                                  </Box>
                                </Tooltip>
                              </Box>
                              <img
                                src={qrAppBlobUrl || scan}
                                alt="QR Code Application"
                                className="previewImage"
                                style={{ maxHeight: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}

                      {reviewData?.qrCodeOnboardUrl && (
                        <Grid item xs={12} sm={6} md={3}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0',
                              bgcolor: '#f8fafc',
                              textAlign: 'center'
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: '#1e293b', mb: 1.5 }}
                            >
                              QR Code Onboard
                            </Typography>
                            <Box
                              className="uploadDocBoxLabel uploadDocBoxLabelFull docPreviewBox"
                              sx={{
                                height: 160,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: '1px solid #cbd5e1',
                                bgcolor: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}
                              onClick={() => {
                                if (qrOnboardBlobUrl) {
                                  setPreviewFileType('image');
                                  setPreviewUrl(qrOnboardBlobUrl);
                                  setOpenPreview(true);
                                }
                              }}
                            >
                              <Box className="previewIconsBox">
                                <Tooltip title="Preview" arrow>
                                  <Box
                                    className="previewEyeIcon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (qrOnboardBlobUrl) {
                                        setPreviewFileType('image');
                                        setPreviewUrl(qrOnboardBlobUrl);
                                        setOpenPreview(true);
                                      }
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </Box>
                                </Tooltip>
                                <Tooltip title="Download" arrow>
                                  <Box
                                    className="previewEyeIcon"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (qrOnboardBlobUrl) {
                                        const link = document.createElement('a');
                                        link.href = qrOnboardBlobUrl;
                                        link.setAttribute('download', 'QRCodeOnboard.png');
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }
                                    }}
                                  >
                                    <BiCloudDownload size={16} />
                                  </Box>
                                </Tooltip>
                              </Box>
                              <img
                                src={qrOnboardBlobUrl || scan}
                                alt="QR Code Onboard"
                                className="previewImage"
                                style={{ maxHeight: '100%', objectFit: 'contain' }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Card>
                </Grid>
              )}
            </Box>
          )}
        </Container>
      </Box>

      {/* Document Preview Modal */}
      <Dialog
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          handleResetZoom();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px', overflow: 'hidden' }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
            px: 3,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
            Document Preview
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Zoom In" arrow>
              <IconButton size="small" onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom Out" arrow>
              <IconButton size="small" onClick={handleZoomOut} disabled={zoomLevel <= 1}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{ minWidth: 45, textAlign: 'center', fontWeight: 600 }}
            >
              {Math.round(zoomLevel * 100)}%
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                setOpenPreview(false);
                handleResetZoom();
              }}
              sx={{ color: '#64748b' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'auto',
            p: 3,
            minHeight: '50vh'
          }}
        >
          {previewUrl && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%'
              }}
            >
              {previewFileType === 'pdf' ? (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: '#f8fafc',
                    p: 2,
                    borderRadius: '8px',
                    overflow: 'auto',
                    maxHeight: '70vh'
                  }}
                >
                  <Document
                    file={previewUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div>Loading PDF...</div>}
                  >
                    <Pages
                      pageNumber={pageNumber}
                      scale={zoomLevel}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </Box>
              ) : (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  onDoubleClick={handleDoubleClick}
                  sx={{
                    width: '100%',
                    maxHeight: 500,
                    objectFit: 'contain',
                    transform: `scale(${zoomLevel})`,
                    transition: 'transform 0.2s ease-in-out',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={handleResetZoom}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b',
              borderColor: '#cbd5e1'
            }}
          >
            Reset Zoom
          </Button>
          <Button
            onClick={() => {
              setOpenPreview(false);
              handleResetZoom();
            }}
            variant="contained"
            size="small"
            sx={{
              borderRadius: '8px',
              bgcolor: '#13519d',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#0d3d75' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default ReviewOnBoarding;
